import React, {useEffect, useRef, useState} from "react";
import {getRandomInt, minimum_volume, Utils} from "../../../components/audioUtils"
import {GalleryWall, VideoOpacityFadeInTime, VideoOpacityFadeOutTime} from "./galleryWall";
import {FullScreenVideo} from "./fullScreenVideo";
import {FadeTo} from "../../../components/fadeTo";
import {GoBackBug, RandomVideoBugContainer} from "./videoBugs";


export const VIDEO_COUNT = 12;
export const VIDEOS_TO_PLAY = 12;
export const PATH_TO_HQ_VIDEOS = "./toi/video/HQ"
export const PATH_TO_LQ_VIDEOS = "./toi/video/LQ"
export const AUTOPLAY = false;

export const VOLUME_FOCUSED = 0; // on gallery item hover, and for fullscreen vid
export const VOLUME_UNFOCUSED = -18; // on gallery item de-hover

export const GALLERY_VIDEO_ATTACK_TIME = 150; // on gallery item hover
export const GALLERY_VIDEO_RELEASE_TIME = 2000; // on gallery item de-hover, and fullscreen vid off 

export const FULLSCREEN_VIDEO_ATTACK_TIME = 50; // on open fullscreen
export const FULLSCREEN_VIDEO_RELEASE_TIME = 2000; // on go back to gallery
export const FULLSCREEN_SWITCH_FADE_TIME = 150; // on choose new video by random bug


export function VideoManager({active, onLoading,  onIsReady}) {
    const [displayFullScreenVideo, setDisplayFullScreenVideo] = useState(false);
    const [focusedVideoSrc, setFocusedVideoSrc] = useState(null);
    const [isReady, setIsReady] = useState(false);

    const fullScreenVideoRef = useRef(null);
    let videoElements = useRef(new Map()); // key: video ID, value: video element
    let fadeHandlers = useRef(new Map()); // key: video ID; value: frameHandlerID
    let isActive = useRef(false);

    useEffect(() => {
        return () => {
            videoElements.current?.forEach((video) => {
                video.pause();
            })
            videoElements.current = new Map();

            const fadeHandlerArray = Array.from(fadeHandlers.current)
            fadeHandlerArray?.forEach((keyPair) => {
                ClearFadeHandler(keyPair[0]);
            })
            fadeHandlers.current = new Map();
            
        }
    }, []);

    useEffect(() => {
        isActive.current = active;  

        if (isActive.current) {
            PlayGalleryVideos(true);
        } else {
            StopGalleryVideos();
        }      
    }, [active]);

    useEffect(() => {
        if (!isActive.current) return;
        
        if (displayFullScreenVideo) {
            StopGalleryVideos();
            PlayFullScreenVideo();
        } else {
            PlayGalleryVideos();
            StopFullScreenVideo();
        }
    }, [displayFullScreenVideo]);

  


    function HandleVideoGalleryCanPlayThrough(_videoElement) {
        // console.log("HandleVideoGalleryCanPlayThrough", _videoElement.id)
        
        ToggleVideoVisibility(_videoElement, false);

        videoElements.current.set(_videoElement.id, _videoElement);
        
        onLoading?.( videoElements.current.size / VIDEO_COUNT);

        if (videoElements.current.size < VIDEO_COUNT) {
            HandleGalleryVideosNotYetLoaded()
        } else {
            HandleAllGalleryVideosLoaded();
        }
    }

    function HandleGalleryVideosNotYetLoaded() {
        // const progress = videoElements.current.size / VIDEO_COUNT;        
        // console.log("HandleGalleryVideosNotYetLoaded", progress)        
    }

    function HandleAllGalleryVideosLoaded() {
        // console.log("HandleAllGalleryVideosLoaded", videoElements.current.size);
        onIsReady?.();
    }
    
  
    function ToggleVideoVisibility(videoElement, visible) {
        // console.log(`ToggleVideoVisibility of ${videoElement.id} to ${visible}`)
        
        videoElement.style.transition = 
            `opacity 
            ${visible ? VideoOpacityFadeInTime : VideoOpacityFadeOutTime} 
            ${visible ? 'ease-in' : 'ease-out'}`;
        
        videoElement.style.opacity = visible ? 1: 0;
        videoElement.tabIndex = visible ? 0 : -1;
        
        setTimeout(() => {
                videoElement.style.pointerEvents = visible ? "auto" : "none";
               
            },
            visible ? 2000 : 0);
    }

    // Video Playback //   

    function PlayGalleryVideos(firstTimePlay = false) {
        // console.log("PlayGalleryVideos")
        videoElements.current.forEach((video) => {            
            // video.currentTime = firstTimePlay ? 0 :  getRandomInt(0, (video.duration * 0.5));            
            video.play();            
            DoFade(video, VOLUME_UNFOCUSED, GALLERY_VIDEO_ATTACK_TIME);
            ToggleVideoVisibility(video, true);
        })
    }

    function StopGalleryVideos() {
        videoElements.current.forEach((video) => {
            DoFade(video, minimum_volume, GALLERY_VIDEO_RELEASE_TIME, function () {
                video.pause();
            });
            
            ToggleVideoVisibility(video, false);
        })
    }

    function PlayFullScreenVideo() {
        if (!fullScreenVideoRef.current) return;
        // console.log("PlayFullScreenVideo", fullScreenVideoRef.current)
        
        fullScreenVideoRef.current.currentTime = 0;
        fullScreenVideoRef.current.play();
        DoFade(fullScreenVideoRef.current, VOLUME_FOCUSED, FULLSCREEN_VIDEO_ATTACK_TIME)
    }

    function StopFullScreenVideo() {
        if (!fullScreenVideoRef.current) return;
        // console.log("StopFullScreenVideo")

        DoFade(fullScreenVideoRef.current, minimum_volume, FULLSCREEN_VIDEO_RELEASE_TIME, function () {
            fullScreenVideoRef?.current.pause();
        })
    }

    function DoFade(element, targetVolume, fadeTime, onEndFade) {
        ClearFadeHandler(element.id);

        // console.log(`Fade called on ${element.id}. Fading from ${atodb(element.volume)} to ${targetVolume} over ${fadeTime}. OnEndFade: ${onEndFade}`)

        FadeTo(element, targetVolume, fadeTime,
            function (animID) {
                // console.log(`adding ${animID} to fadeHandlers with key ${element.id}`)
                fadeHandlers.current.set(element.id, animID);
            },
            function () {
                onEndFade?.();
            })
    }

    function ClearFadeHandler(elementID) {
        // console.log("stopping fade for " + elementID)

        let animationFrameId = fadeHandlers.current.get(elementID);
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            fadeHandlers.current.delete(elementID);
        }
    }


    // Gallery Video Handlers //

    function HandleGalleryVideoHoverStart(_videoElement) {
        // console.log("HandleGalleryVideoHoverStart", _videoElement)

        DoFade(_videoElement, VOLUME_FOCUSED, GALLERY_VIDEO_ATTACK_TIME);
        SetGalleryItemBorders(_videoElement, true)
    }

    function HandleGalleryVideoHoverEnd(_videoElement) {
        if (displayFullScreenVideo) return;
        // console.log("HandleGalleryVideoHoverEnd", _videoElement)

        DoFade(_videoElement, VOLUME_UNFOCUSED, GALLERY_VIDEO_RELEASE_TIME);
        SetGalleryItemBorders(_videoElement, false);
    }

    function HandleGalleryVideoClick(_videoElement) {
        // console.log("on click video element", _videoElement)

        setDisplayFullScreenVideo(true);
        
        // set focused video source to clicked gallery video source
        SetFullScreenVideoSource(extractVideoNumberFromID(_videoElement.id));   
        
        SetGalleryItemBorders(_videoElement, false);
    }
    
    function SetFullScreenVideoSource(videoNumber) {
        const src = `${PATH_TO_HQ_VIDEOS}/video_${videoNumber}.mp4`;
        setFocusedVideoSrc(src);
    }
    
    function SetGalleryItemBorders(_videoElement, borderVisibility)    {
        _videoElement.style.border = borderVisibility ? "1px solid white" : "none";
    }


    // Fullscreen Video Handlers    
    function HandleCanPlayThroughFullscreenVideo(_videoElement) {
        // console.log("HandleCanPlayThroughFullscreenVideo", _videoElement);
    }

    function HandleReturnToGalleryView(_videoElement) {
        // console.log("HandleReturnToGalleryView", _videoElement)
        setDisplayFullScreenVideo(false);
    }

    function HandleRandomBugClick() {
        // console.log("HandleRandomBugClick")

        const current = fullScreenVideoRef.current;

        // choose a new video to play that isn't this one
        let randomVideo;
        do {
            const randomIndex = Utils.getRandomInt(0, videoElements.current.size - 1);
            randomVideo = Array.from(videoElements.current.values())[randomIndex];
        } while (randomVideo.src === current.src);

        // console.log("original src: " + current.src + " // random video src: " + randomVideo.src)
        // console.log(`original id = ${current.id}  / random video id = ${randomVideo.id}`)

        DoFade(fullScreenVideoRef.current, minimum_volume, FULLSCREEN_SWITCH_FADE_TIME, function () {
            setDisplayFullScreenVideo(true);            
            
            SetFullScreenVideoSource(extractVideoNumberFromID(randomVideo.id));
            
            setTimeout(() => {
                PlayFullScreenVideo();
            }, 0)
        })
    }
    
    function extractVideoNumberFromID(src)
    {
        const regex = /video_(\d+)_/;
        const match = src.match(regex);
        return match ? match[1] : null;
    }

    return (
        <>
            <div id={"galleryView"} style={{
                width: "100%",
                height: "100%",
            }}>
                <GalleryWall
                    onCanPlayThrough={HandleVideoGalleryCanPlayThrough}
                    onClick={HandleGalleryVideoClick}
                    onHoverStart={HandleGalleryVideoHoverStart}
                    onHoverEnd={HandleGalleryVideoHoverEnd}
                />
            </div>

            <div id={"focusView"}
                 style={{
                     width: "100%",
                     height: "100%",
                     
                     opacity: displayFullScreenVideo ? 1 : 0,
                     pointerEvents: displayFullScreenVideo ? "auto" : "none",
                     transition:
                             `opacity  
                             ${displayFullScreenVideo ? VideoOpacityFadeInTime : VideoOpacityFadeOutTime} 
                             ease-out`,

                 }}>
                <FullScreenVideo
                    src={focusedVideoSrc}
                    setRef={(r) => fullScreenVideoRef.current = r}
                />

                <GoBackBug
                    onClick={HandleReturnToGalleryView}
                />

                <RandomVideoBugContainer
                    numberOfBugs={Utils.getRandomInt(2, 4)}
                    onClick={HandleRandomBugClick}
                    shouldDisplayBugs={displayFullScreenVideo}
                />
            </div>
        </>
    )
}
